import { isEqual } from 'lodash'

import { type UnsavedPendingItem, type UnsavedPendingItemKey } from './types'

const LEGACY_DRAFT_STORAGE_PREFIXES = ['action-item-edit-draft:', 'unsaved-changes:'] as const

export const cleanupLegacyDraftStorage = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const storage = window.localStorage
    const matchingKeys: string[] = []

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key && LEGACY_DRAFT_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        matchingKeys.push(key)
      }
    }

    matchingKeys.forEach((key) => {
      try {
        storage.removeItem(key)
      } catch (_error) {
        // Keep cleanup non-blocking and continue with the remaining keys.
      }
    })
  } catch (_error) {
    // Storage can be unavailable in blocked or private browsing contexts.
  }
}

export const getEnabledUnsavedPendingCapture = (
  enabled: boolean,
  capture: (item: UnsavedPendingItem) => void
): ((item: UnsavedPendingItem) => void) | undefined => (enabled ? capture : undefined)

export const removeUnsavedPendingIfEnabled = (
  enabled: boolean,
  removePending: (key: UnsavedPendingItemKey) => void,
  key: UnsavedPendingItemKey
): void => {
  if (enabled) {
    removePending(key)
  }
}

export const hasRegisteredUnsavedPendingCapture = (capture?: (item: UnsavedPendingItem) => void): boolean =>
  typeof capture === 'function'

/**
 * Normalizes text for comparison: trims whitespace and converts to lowercase.
 * Handles undefined/null gracefully.
 */
const normalizeText = (value: string | undefined | null): string => {
  return (value ?? '').trim().toLowerCase()
}

/**
 * Normalizes an array for comparison: sorts and removes duplicates.
 * Handles undefined/null gracefully.
 */
const normalizeArray = (value: string[] | undefined | null): string[] => {
  if (!value || !Array.isArray(value)) {
    return []
  }
  return [...value].sort()
}

/**
 * Represents the subset of form data relevant for detecting meaningful changes.
 */
interface DraftFields {
  title?: string | null
  description?: string | null
  _assignedTeamIds?: string[] | null
  _assignedUserIds?: string[] | null
  _workflowIds?: string[] | null
}

/**
 * Represents original action item data from the server.
 */
interface OriginalFields {
  title?: string | null
  description?: string | null
  /**
   * Server returns assignments as edges/nodes structure.
   * Extract team IDs from actionItemAssignments.
   */
  actionItemAssignments?: {
    nodes?: ReadonlyArray<{
      teamId?: string | null
      team?: { rowId?: string | null } | null
      user?: { rowId?: string | null } | null
    }> | null
  } | null
  /**
   * Server returns assigned users as edges/nodes structure.
   * Extract user IDs from actionItemAssignedUsers.
   */
  actionItemAssignedUsers?: {
    nodes?: ReadonlyArray<{
      userId?: string | null
      user?: { rowId?: string | null } | null
    }> | null
  } | null
  /**
   * Server returns workflows as edges/nodes structure.
   * Extract workflow IDs from actionItemWorkflows.
   */
  actionItemWorkflows?: {
    nodes?: ReadonlyArray<{
      workflowId?: string | null
      workflow?: { id?: string | null } | null
    }> | null
  } | null
}

/**
 * Extracts team IDs from server's actionItemAssignments structure.
 */
const extractTeamIds = (original: OriginalFields): string[] => {
  const nodes = original.actionItemAssignments?.nodes
  if (!nodes) {
    return []
  }
  return nodes.map((n) => n?.teamId || n?.team?.rowId).filter((id): id is string => !!id)
}

/**
 * Extracts user IDs from server's actionItemAssignedUsers structure.
 */
const extractUserIds = (original: OriginalFields): string[] => {
  const nodes = original.actionItemAssignedUsers?.nodes
  if (!nodes) {
    return []
  }
  return nodes.map((n) => n?.userId || n?.user?.rowId).filter((id): id is string => !!id)
}

/**
 * Extracts workflow IDs from server's actionItemWorkflows structure.
 */
const extractWorkflowIds = (original: OriginalFields): string[] => {
  const nodes = original.actionItemWorkflows?.nodes
  if (!nodes) {
    return []
  }
  return nodes.map((n) => n?.workflowId || n?.workflow?.id).filter((id): id is string => !!id)
}

/**
 * Checks if a draft has meaningful changes compared to the original data.
 *
 * Compares (when available):
 * - title (normalized text)
 * - description (normalized text)
 * - _assignedTeamIds (sorted array comparison)
 * - _assignedUserIds (sorted array comparison)
 * - _workflowIds (sorted array comparison)
 *
 * For fields not available in the original (e.g., lightweight Grid fragments),
 * we skip comparison for that field — a draft that only differs in unavailable
 * fields is NOT considered meaningful since we can't verify the delta.
 *
 * @param draft - The parsed draft from localStorage
 * @param original - The original action item data from the server (may be partial)
 * @returns true if there are meaningful changes, false otherwise
 */
export const hasMeaningfulDraftChanges = (
  draft: DraftFields | null | undefined,
  original: OriginalFields | null | undefined
): boolean => {
  // No draft means no changes
  if (!draft) {
    return false
  }

  // No original means we can't compare; treat draft as meaningful
  if (!original) {
    return true
  }

  // Compare title (always available)
  if (normalizeText(draft.title) !== normalizeText(original.title)) {
    return true
  }

  // Compare description — only if original has it (detailed fragments)
  // Check if description is explicitly present (even if empty string) vs undefined
  if ('description' in original) {
    if (normalizeText(draft.description) !== normalizeText(original.description)) {
      return true
    }
  }

  // Compare team assignments — only if original has actionItemAssignments
  if (original.actionItemAssignments) {
    const draftTeamIds = normalizeArray(draft._assignedTeamIds)
    const originalTeamIds = normalizeArray(extractTeamIds(original))
    if (!isEqual(draftTeamIds, originalTeamIds)) {
      return true
    }
  }

  // Compare user assignments — only if original has actionItemAssignedUsers
  if (original.actionItemAssignedUsers) {
    const draftUserIds = normalizeArray(draft._assignedUserIds)
    const originalUserIds = normalizeArray(extractUserIds(original))
    if (!isEqual(draftUserIds, originalUserIds)) {
      return true
    }
  }

  // Compare workflow assignments — only if original has actionItemWorkflows
  if (original.actionItemWorkflows) {
    const draftWorkflowIds = normalizeArray(draft._workflowIds)
    const originalWorkflowIds = normalizeArray(extractWorkflowIds(original))
    if (!isEqual(draftWorkflowIds, originalWorkflowIds)) {
      return true
    }
  }

  return false
}

/**
 * Clears an action item draft from localStorage.
 */
export const clearActionItemDraftStorage = (actionItemId: string): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.removeItem(`action-item-edit-draft:${actionItemId}`)
  } catch (_error) {
    // no-op
  }
}

/**
 * Reads and parses an action item draft from localStorage.
 * Returns null if no draft exists or parsing fails.
 */
export const readActionItemDraftRaw = (actionItemId: string): DraftFields | null => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = window.localStorage.getItem(`action-item-edit-draft:${actionItemId}`)
    if (!raw) {
      return null
    }
    const result = JSON.parse(raw) as DraftFields
    return result
  } catch (_error) {
    return null
  }
}
